<script setup>
import { router, useForm } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  user: { type: Object, required: true },
  companies: { type: Array, default: () => [] },
  // RED-328: { override: bool|null, company_default: bool, effective: bool }
  analytics: { type: Object, default: () => ({ override: null, company_default: false, effective: false }) },
})

const { t } = useI18n()
const toast = useToast()
const confirm = useConfirm()

const form = useForm({
  company_id: props.user.company?.id ?? null,
})

// RED-328: per-user web analytics switch (inherit / on / off), optimistic with revert
const toState = (override) => (override === null || override === undefined ? 'inherit' : override ? 'on' : 'off')
const analyticsState = ref(toState(props.analytics.override))
const savingAnalytics = ref(false)
const analyticsOptions = computed(() => [
  { value: 'inherit', label: t('messages.admin.user.analytics_inherit') },
  { value: 'on', label: t('messages.admin.user.analytics_on') },
  { value: 'off', label: t('messages.admin.user.analytics_off') },
])
const analyticsEffectiveText = computed(() => {
  const inherit = analyticsState.value === 'inherit'
  const effective = inherit ? props.analytics.company_default : analyticsState.value === 'on'
  const source = t(inherit ? 'messages.admin.user.analytics_source_company' : 'messages.admin.user.analytics_source_user')
  return t(effective ? 'messages.admin.user.analytics_effective_on' : 'messages.admin.user.analytics_effective_off', { source })
})

const onAnalyticsChange = (state) => {
  if (!state || state === analyticsState.value) return
  const previous = analyticsState.value
  analyticsState.value = state
  savingAnalytics.value = true

  router.put(
    route('admin.user.analytics', props.user.id),
    { state },
    {
      preserveScroll: true,
      onSuccess: () => {
        toast.add({
          severity: 'success',
          summary: t('messages.updated'),
          detail: t('messages.admin.user.analytics_updated_success'),
          life: 3000,
        })
      },
      onError: () => {
        analyticsState.value = previous
        toast.add({
          severity: 'error',
          summary: t('messages.error'),
          detail: t('messages.admin.user.analytics_updated_error'),
          life: 3000,
        })
      },
      onFinish: () => {
        savingAnalytics.value = false
      },
    },
  )
}

const submit = () => {
  confirm.require({
    message: t('messages.admin.user.change_company_warning'),
    header: t('messages.admin.user.tabs.company'),
    accept: () => {
      form.put(route('admin.user.company', props.user.id), {
        preserveScroll: true,
        onSuccess: () => {
          toast.add({
            severity: 'success',
            summary: t('messages.updated'),
            detail: t('messages.admin.user.company_updated_success'),
            life: 3000,
          })
        },
      })
    },
  })
}
</script>

<template>
  <Card>
    <template #title>{{ $t('messages.admin.user.tabs.company') }}</template>
    <template #content>
      <div class="mb-3">
        <div class="text-sm text-gray-600 mb-1">{{ $t('messages.admin.user.current_company') }}</div>
        <div class="text-base">{{ user.company?.name || '—' }}</div>
      </div>
      <Fluid>
        <form class="flex items-end gap-2" @submit.prevent="submit">
          <div class="flex-1">
            <label class="text-sm text-gray-600">{{ $t('messages.admin.select.company') }}</label>
            <Select
              v-model="form.company_id"
              :options="companies"
              optionLabel="name"
              optionValue="id"
            />
          </div>
          <Button type="submit" severity="warn" :disabled="form.processing || !form.company_id" :loading="form.processing" class="w-auto!">
            {{ $t('messages.save') }}
          </Button>
        </form>
      </Fluid>
      <div class="mt-6">
        <div class="text-sm text-gray-600 mb-1">{{ $t('messages.admin.user.analytics') }}</div>
        <SelectButton
          :model-value="analyticsState"
          :options="analyticsOptions"
          optionLabel="label"
          optionValue="value"
          :allow-empty="false"
          :disabled="savingAnalytics"
          data-testid="user-analytics-state"
          @update:model-value="onAnalyticsChange"
        />
        <div class="text-sm text-gray-600 mt-2" data-testid="user-analytics-effective">{{ analyticsEffectiveText }}</div>
      </div>
    </template>
  </Card>
</template>
